// @flow
import actionCreators from "@modules/reporting/actionCreator";
import type { Action } from "redux-action";
import { all, call, put, takeEvery } from "redux-saga/effects";
import { push } from "connected-react-router";
import { route } from "@routes/helper";
import http from "@http";
import {
  GetAbsenceRateTrendsDefaultReports,
  GetAbsenceRateTrendsMyReports,
  GetAbsenceRateTrendsReportData,
  GetAbsenceRateTrendsReportSchema,
  CreateAbsenceRateTrendsReport,
  UpdateAbsenceRateTrendsReport,
  DeleteAbsenceRateTrendsReport,
  ExportAbsenceRateTrendsReport,
  ExportAbsenceRateTrendsReportSuccess,
  GetAbsenceRateTrendBreakdown,
  ExportAbsenceRateTrendBreakdown,
} from "@modules/reporting/constants/actions";
import type { AbsenceRateReport } from "@modules/reporting/reducer/trends/absence-rate/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { downloadCsvFile } from "@sagas/utils";
import { parseSchema } from "@utils/schema";
import camelcase from "camelcase";

type TrendsReportsRequest = $Exact<{
  clientId: string,
}>;

export function* onGetAbsenceRateTrendsDefaultReports({
  payload,
}: Action<TrendsReportsRequest>): Generator<*, void, { data: Array<AbsenceRateReport> }> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/trends/client/${payload.clientId}/trends/absence-rate/default-reports`,
    );
    yield put(actionCreators.GetAbsenceRateTrendsDefaultReportsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAbsenceRateTrendsDefaultReportsError(e.response));
  }
}

export function* onGetAbsenceRateTrendsMyReports({
  payload,
}: Action<TrendsReportsRequest>): Generator<*, void, { data: Array<AbsenceRateReport> }> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/trends/client/${payload.clientId}/trends/absence-rate/my-saved-reports`,
    );
    yield put(actionCreators.GetAbsenceRateTrendsMyReportsSuccess(response.data));
    yield put(actionCreators.ReSetAbsenceRateTrendsReportToSave());
  } catch (e) {
    yield put(actionCreators.GetAbsenceRateTrendsMyReportsError(e.response));
  }
}

type TrendsReportDataRequest = $Exact<{
  ...TrendsReportsRequest,
  report: AbsenceRateReport,
  noRedirect?: boolean,
}>;

export function* onGetAbsenceRateTrendsReportData({
  payload,
}: Action<TrendsReportDataRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.report) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.post,
      `/api/v1/trends/client/${payload.clientId}/trends/absence-rate`,
      payload.report.filters,
    );

    yield put(
      actionCreators.GetAbsenceRateTrendsReportDataSuccess({
        details: payload.report,
        reportData: response.data,
      }),
    );

    if (!payload.noRedirect) {
      yield put(
        push(route("REPORTING_TRENDS_ABSENCE_RATE_REPORT", { clientId: payload.clientId })),
      );
    }
  } catch (e) {
    yield put(actionCreators.GetAbsenceRateTrendsReportDataError(e.response));
  }
}

export function* onGetAbsenceRateTrendsReportSchema({
  payload,
}: Action<TrendsReportsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const schema = yield parseSchema("AbsenceRateTrendsReportFiltersDTO", {
      clientId: payload.clientId,
    });

    yield put(actionCreators.GetAbsenceRateTrendsReportSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetAbsenceRateTrendsReportSchemaError(e.response));
  }
}

type CreateAbsenceRateTrendsReportRequest = $Exact<{
  clientId: number,
  filters: Object,
  name: string,
}>;

export function* onCreateAbsenceRateTrendsReport({
  payload,
}: Action<CreateAbsenceRateTrendsReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.filters || !payload.name) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.post,
      `/api/v1/trends/client/${payload.clientId}/trends/absence-rate/my-saved-reports`,
      {
        name: payload.name,
        filters: payload.filters,
      },
    );

    yield all([
      put(actionCreators.CreateAbsenceRateTrendsReportSuccess(response.data)),
      put(actionCreators.ToggleSaveReportModal({ visible: false })),
      put(actionCreators.GetAbsenceRateTrendsMyReports({ clientId: payload.clientId })),
      put(
        actionCreators.GetAbsenceRateTrendsReportData({
          clientId: payload.clientId,
          report: payload,
        }),
      ),
      put(sendSuccessMessage("REPORT_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.CreateAbsenceRateTrendsReportError(e.response));
  }
}

type UpdateAbsenceRateTrendsReportRequest = $Exact<{
  clientId: number,
  reportId: number,
  data: Object,
  shouldFetchReportData?: boolean,
}>;

export function* onUpdateAbsenceRateTrendsReport({
  payload,
}: Action<UpdateAbsenceRateTrendsReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.reportId || !payload.data) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.put,
      `/api/v1/trends/client/${payload.clientId}/trends/absence-rate/my-saved-reports/${payload.reportId}`,
      payload.data,
    );

    const additionalActions = payload.shouldFetchReportData
      ? [
          put(
            actionCreators.GetAbsenceRateTrendsReportData({
              clientId: payload.clientId,
              report: response.data,
              noRedirect: true,
            }),
          ),
        ]
      : [];

    yield all([
      put(actionCreators.UpdateAbsenceRateTrendsReportSuccess(response.data)),
      put(actionCreators.ToggleSaveReportModal({ visible: false })),
      put(actionCreators.GetAbsenceRateTrendsMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage("REPORT_UPDATED")),
      ...additionalActions,
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateAbsenceRateTrendsReportError(e.response));
  }
}

type DeleteAbsenceRateTrendsReportRequest = $Exact<{
  clientId: number,
  reportId: number,
  successMessage: string,
}>;

export function* onDeleteAbsenceRateTrendsReport({
  payload,
}: Action<DeleteAbsenceRateTrendsReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.reportId || !payload.successMessage) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield call(
      http.delete,
      `/api/v1/trends/client/${payload.clientId}/trends/absence-rate/my-saved-reports/${payload.reportId}`,
    );

    yield all([
      put(actionCreators.DeleteAbsenceRateTrendsReportSuccess()),
      put(actionCreators.GetAbsenceRateTrendsMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage(payload.successMessage)),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteAbsenceRateTrendsReportError(e.response));
  }
}

type ExportAbsenceReasonTrendsRequest = $Exact<{
  ...TrendsReportsRequest,
  filters: *,
}>;

export function* onExportAbsenceRateTrendsReport({
  payload,
}: Action<ExportAbsenceReasonTrendsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.filters) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.post,
      `/api/v1/trends/client/${payload.clientId}/trends/absence-rate/export`,
      payload.filters,
      {
        headers: {
          Accept: "text/csv",
        },
      },
    );

    yield put(actionCreators.ExportAbsenceRateTrendsReportSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.ExportAbsenceRateTrendsReportError(e.response));
  }
}

export function* onExportAbsenceRateTrendsReportSuccess({
  payload,
}: Action<any>): Generator<*, void, any> {
  yield downloadCsvFile(payload, "absence-rate-trends-report.csv");
}

type TrendsBreakdownDataRequest = $Exact<{
  ...TrendsReportsRequest,
  filters: Object,
  details?: Object,
}>;

export function* onGetAbsenceRateTrendBreakdown({
  payload,
}: Action<TrendsBreakdownDataRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.filters) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.post,
      `/api/v1/trends/client/${payload.clientId}/trends/absence-rate/breakdown`,
      payload.filters,
    );

    yield put(
      actionCreators.GetAbsenceRateTrendBreakdownSuccess({
        details: payload.details,
        filters: payload.filters,
        list: response.data,
      }),
    );
  } catch (e) {
    yield put(actionCreators.GetAbsenceRateTrendBreakdownError(e.response));
  }
}

export function* onExportAbsenceRateTrendBreakdown({
  payload,
}: Action<TrendsBreakdownDataRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.filters || !payload.details) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.post,
      `/api/v1/trends/client/${payload.clientId}/trends/absence-rate/breakdown/export`,
      payload.filters,
      {
        headers: {
          Accept: "text/csv",
        },
      },
    );

    yield put(actionCreators.ExportAbsenceRateTrendBreakdownSuccess());
    yield downloadCsvFile(
      response.data,
      `TrendAbsenceRateBreakdown-client_${payload.clientId}-${camelcase(payload?.filters?.groupBy, {
        pascalCase: true,
      }) || ""}_${payload?.details?.name || ""}-dateFrom_${payload?.details?.fromDate ||
        ""}-dateTo_${payload?.details?.toDate || ""}.csv`,
    );
    yield put(sendSuccessMessage("TREND_ABSENCE_RATE_BREAKDOWN_EXPORT_SUCCESS"));
  } catch (e) {
    yield put(actionCreators.ExportAbsenceRateTrendBreakdownError(e.response));
  }
}

export function* AbsenceRateSaga(): Generator<*, void, void> {
  yield all([
    takeEvery(GetAbsenceRateTrendsDefaultReports, onGetAbsenceRateTrendsDefaultReports),
    takeEvery(GetAbsenceRateTrendsMyReports, onGetAbsenceRateTrendsMyReports),
    takeEvery(GetAbsenceRateTrendsReportData, onGetAbsenceRateTrendsReportData),
    takeEvery(GetAbsenceRateTrendsReportSchema, onGetAbsenceRateTrendsReportSchema),
    takeEvery(CreateAbsenceRateTrendsReport, onCreateAbsenceRateTrendsReport),
    takeEvery(UpdateAbsenceRateTrendsReport, onUpdateAbsenceRateTrendsReport),
    takeEvery(DeleteAbsenceRateTrendsReport, onDeleteAbsenceRateTrendsReport),
    takeEvery(ExportAbsenceRateTrendsReport, onExportAbsenceRateTrendsReport),
    takeEvery(ExportAbsenceRateTrendsReportSuccess, onExportAbsenceRateTrendsReportSuccess),
    takeEvery(GetAbsenceRateTrendBreakdown, onGetAbsenceRateTrendBreakdown),
    takeEvery(ExportAbsenceRateTrendBreakdown, onExportAbsenceRateTrendBreakdown),
  ]);
}

export default AbsenceRateSaga;
